import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { completedMonths, statsFor, keyOf } from "../utils/monthlyStats";
import captureElement, { FORMATS } from "../utils/captureCard";
import exportCanvas from "../utils/exportImage";
import { track } from "../utils/analytics";
import "./MonthlyStats.css";

const COLORS = ["#3a2e5c", "#2c4a52", "#5a3a2e", "#2e3f5c", "#4a2e4a"];
const N = ({ children }) => <span className="mst-num">{children}</span>;
const LIST_MAX = 12;

/* 규격 판에 넣으면 카드가 줄어든다. 목록이 길면 글자가 인스타에서 못 읽을
   만큼 작아져서, 찍는 사본에서만 피드 6권·스토리 8권까지 두고 나머지는
   '외 N권' 으로 접는다. 화면의 목록은 그대로다. */
export const LIST_CAP = { feed: 6, story: 8 };
export function trimBookList(root, doc, cap, total) {
  const rows = [...root.querySelectorAll(".mst-bl > li")];
  if (rows.length <= cap) return;
  rows.slice(cap).forEach((li) => li.remove());
  root.querySelector(".mst-bl-more")?.remove();
  const more = doc.createElement("div");
  more.className = "mst-bl-more";
  more.innerHTML = `외 <span class="mst-num">${total - cap}</span>권`;
  root.querySelector(".mst-bl").after(more);
}

/** 좌표 화면과 같은 방향: 세로축 위(0)·아래(100), 가로축 왼(0)·오른(100) */
function Plane({ plane }) {
  const S = 92, PX = 26, PY = 12;
  const { axes, pts, center } = plane;
  return (
    <svg className="mst-plane" width={S + PX * 2} height={S + PY * 2} viewBox={`0 0 ${S + PX * 2} ${S + PY * 2}`} aria-hidden="true">
      <rect x={PX} y={PY} width={S} height={S} fill="none" stroke="rgba(245,240,232,.14)" />
      <line x1={PX} y1={PY + S / 2} x2={PX + S} y2={PY + S / 2} stroke="rgba(245,240,232,.07)" />
      <line x1={PX + S / 2} y1={PY} x2={PX + S / 2} y2={PY + S} stroke="rgba(245,240,232,.07)" />
      <text x={PX + S / 2} y={PY - 3} fontSize="7" fill="rgba(245,240,232,.5)" textAnchor="middle">{axes.y[0]}</text>
      <text x={PX + S / 2} y={PY + S + 9} fontSize="7" fill="rgba(245,240,232,.5)" textAnchor="middle">{axes.y[1]}</text>
      <text x={PX - 3} y={PY + S / 2 + 2.5} fontSize="7" fill="rgba(245,240,232,.5)" textAnchor="end">{axes.x[0]}</text>
      <text x={PX + S + 3} y={PY + S / 2 + 2.5} fontSize="7" fill="rgba(245,240,232,.5)" textAnchor="start">{axes.x[1]}</text>
      {pts.map(([b, c], i) => (
        <circle key={i} cx={PX + (b / 100) * S} cy={PY + (c / 100) * S} r="3" fill="#f5f0e8" opacity=".85" />
      ))}
      {pts.length > 1 && (
        <circle cx={PX + (center[0] / 100) * S} cy={PY + (center[1] / 100) * S} r="9"
          fill="rgba(201,168,76,.18)" stroke="#c9a84c" strokeWidth="1" />
      )}
    </svg>
  );
}

/**
 * 월별 탭 맨 위의 한 달 카드. 달은 완독일로 나눈다.
 * 한 권만 읽은 달이 74개 달 중 33개라, 한 권이어도 비어 보이지 않게 둔다 —
 * 작가·이어 읽기처럼 한 권으로는 뜻이 없는 줄은 감춘다.
 *
 * 달 제목을 누르면 열두 달 표가 열린다. 기록 있는 달만 누를 수 있다.
 * '이미지로 내보내기' 는 이 카드를 보이는 그대로 찍는다(달 제목 포함,
 * 화살표·버튼 제외).
 */
export default function MonthlyStats({ books, uid, name, onBookClick, canExport }) {
  const months = useMemo(() => completedMonths(books, uid), [books, uid]);
  const counts = useMemo(() => {
    const c = {};
    for (const b of books || []) {
      const k = keyOf(b.completedBy?.[uid]);
      if (k) c[k] = (c[k] || 0) + 1;
    }
    return c;
  }, [books, uid]);
  const years = useMemo(() => [...new Set(months.map((k) => k.slice(0, 4)))], [months]);

  const [idx, setIdx] = useState(0);
  const [picker, setPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [manual, setManual] = useState(null);
  const [fmt, setFmt] = useState("feed");
  const captureRef = useRef(null);

  useEffect(() => { setIdx(0); setPicker(false); }, [uid]);
  useEffect(() => {
    if (!picker) return undefined;
    const onKey = (e) => e.key === "Escape" && setPicker(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [picker]);

  const key = months.length ? months[Math.min(idx, months.length - 1)] : null;
  const s = useMemo(() => (key ? statsFor(books, uid, key) : null), [books, uid, key]);
  if (!s) return null;

  const diff = s.n - s.prevN;
  const older = idx < months.length - 1;
  const newer = idx > 0;

  const trimList = (root, doc) => trimBookList(root, doc, LIST_CAP[fmt] || LIST_MAX, s.books.length);

  const handleExport = async () => {
    if (!captureRef.current || busy) return;
    setBusy(true); setMsg(""); setPicker(false);
    try {
      const canvas = await captureElement(captureRef.current, { scale: 3, format: fmt, prepare: trimList });
      const how = await exportCanvas(canvas, `muroo-${s.y}-${String(s.m).padStart(2, "0")}-${fmt}.png`);
      track("monthly_export", { format: fmt });
      if (how === "manual") setManual(canvas.toDataURL("image/png"));
      else if (how === "downloaded") setMsg("저장했어요");
    } catch (e) {
      console.error("월별 카드 내보내기 실패:", e?.message);
      setMsg("이미지를 만들지 못했어요");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mst">
      <div className="mst-capture" ref={captureRef} data-capture-root="1">
        <div className="mst-nav">
          <button className="mst-nav-b" data-no-capture="1" onClick={() => setIdx(idx + 1)} disabled={!older} aria-label="이전 달">‹</button>
          <button className="mst-month" onClick={() => setPicker((v) => !v)} aria-expanded={picker} aria-haspopup="dialog">
            <N>{s.y}</N>년 <N>{s.m}</N>월<span className="mst-caret" data-no-capture="1" aria-hidden="true">▾</span>
          </button>
          <button className="mst-nav-b" data-no-capture="1" onClick={() => setIdx(idx - 1)} disabled={!newer} aria-label="다음 달">›</button>

          {picker && (
            <>
              <div className="mst-pk-back" data-no-capture="1" onClick={() => setPicker(false)} />
              <div className="mst-picker" data-no-capture="1" role="dialog" aria-label="달 고르기">
                {years.map((y) => (
                  <div key={y} className="mst-pk-year">
                    <div className="mst-pk-y"><N>{y}</N>년</div>
                    <div className="mst-pk-grid">
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
                        const k = `${y}-${String(m).padStart(2, "0")}`;
                        const c = counts[k] || 0;
                        return (
                          <button key={k} className={`mst-pk-m${k === key ? " on" : ""}`} disabled={!c}
                            onClick={() => { setIdx(months.indexOf(k)); setPicker(false); }}>
                            <span><N>{m}</N>월</span>
                            {c > 0 && <small><N>{c}</N>권</small>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="mst-card">
          <div className="mst-big">
            <span className="mst-n mst-num">{s.n}</span>
            <span className="mst-u">권을 읽었어요</span>
            <span className={`mst-delta ${diff > 0 ? "up" : diff < 0 ? "down" : "same"}`}>
              {diff === 0 ? "지난달과 같아요" : <>지난달보다 <N>{diff > 0 ? `+${diff}` : `−${-diff}`}</N></>}
            </span>
          </div>

          {/* 표지를 누르면 그 책으로 */}
          <div className="mst-covers">
            {s.books.slice(0, 8).map((b, i) => (
              <button key={b.id} className="mst-cvb" aria-label={b.title}
                onClick={() => onBookClick && onBookClick(b.id)}>
                {b.cover
                  ? <img className="mst-cv" src={b.cover} alt={b.title} data-cover="1" />
                  : <div className="mst-cv mst-cv-none" style={{ background: COLORS[i % COLORS.length] }}>{b.title}</div>}
              </button>
            ))}
            {s.books.length > 8 && <div className="mst-cv mst-cv-more">+<N>{s.books.length - 8}</N></div>}
          </div>

          <div className="mst-sec">
            <div className="mst-lbl">이번 달의 결</div>
            {s.plane ? (
              <div className="mst-plane-row">
                <Plane plane={s.plane} />
                <div className="mst-read">
                  {s.plane.reading}
                  <small>
                    좌표 <N>{s.coords}</N>권 / <N>{s.n}</N>권
                    {s.plane.other > 0 && <> · {s.plane.family === "fiction" ? "비소설" : "소설"} <N>{s.plane.other}</N>권은 축이 달라 따로</>}
                  </small>
                </div>
              </div>
            ) : (
              <div className="mst-empty">이 달엔 감정 좌표를 찍은 책이 없어요</div>
            )}
          </div>

          <div className="mst-sec">
            <div className="mst-lbl">장르</div>
            <div className="mst-chips">
              {s.genres.map(([g, n]) => <span key={g} className="mst-chip">{g}<b className="mst-num">{n}</b></span>)}
            </div>
          </div>

          {/* 표지만으로는 무슨 책인지 모른다 — 공유된 그림을 보는 사람에게 제목과 저자를 준다 */}
          <div className="mst-sec">
            <div className="mst-lbl">읽은 책</div>
            <ol className="mst-bl">
              {s.books.slice(0, LIST_MAX).map((b) => (
                <li key={b.id}>
                  <button className="mst-bl-row" onClick={() => onBookClick && onBookClick(b.id)}>
                    <span className="mst-bl-d"><N>{Number(b.day.slice(3))}</N>일</span>
                    <span className="mst-bl-t">{b.title}{b.author && <small>{b.author}</small>}</span>
                  </button>
                </li>
              ))}
            </ol>
            {s.books.length > LIST_MAX && <div className="mst-bl-more">외 <N>{s.books.length - LIST_MAX}</N>권</div>}
          </div>

          {s.streak >= 2 && <div className="mst-streak">✦ <N>{s.streak}</N>달째 매달 한 권 이상 읽고 있어요</div>}

          {/* 규격을 버튼 위에 둔다. 누른 뒤에 눈에 들어오면 이미 나간 것을 다시 만든다. */}
          {canExport && (
            <div className="mst-fmts" data-no-capture="1" role="group" aria-label="규격">
              {Object.values(FORMATS).map((f) => (
                <button key={f.key} className={`mst-fmt${fmt === f.key ? " on" : ""}`}
                  onClick={() => setFmt(f.key)} aria-pressed={fmt === f.key}>{f.label}</button>
              ))}
            </div>
          )}
          {canExport && (
            <button className="mst-export" data-no-capture="1" onClick={handleExport} disabled={busy}>
              {busy ? "만드는 중…" : "이 달을 이미지로 내보내기"}
            </button>
          )}
          {msg && <div className="mst-msg" data-no-capture="1">{msg}</div>}
        </div>
      </div>

      {/* 공유도 다운로드도 막힌 환경 — 그림을 띄워 길게 눌러 저장하게 한다 */}
      {manual && (
        <div className="mst-manual" onClick={() => setManual(null)}>
          <div className="mst-manual-box" onClick={(e) => e.stopPropagation()}>
            <img src={manual} alt={`${s.y}년 ${s.m}월 독서`} />
            <div className="mst-manual-t">그림을 길게 눌러 저장해주세요</div>
            <button className="mst-manual-close" onClick={() => setManual(null)}>닫기</button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * 월별 탭에는 이 막대만 둔다. 카드를 펼쳐두면 탭을 너무 많이 차지해
 * 아래 책 목록이 한참 밀린다. 누르면 시트로 카드가 열린다.
 * 막대는 기록이 있는 가장 최근 달을 가리킨다.
 */
export function MonthlyStatsLauncher(props) {
  const { books, uid } = props;
  const months = useMemo(() => completedMonths(books, uid), [books, uid]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!months.length) return null;
  const latest = months[0];
  const [y, m] = latest.split("-").map(Number);
  const n = (books || []).filter((b) => keyOf(b.completedBy?.[uid]) === latest).length;
  const thisYear = new Date().getFullYear();

  return (
    <>
      <button className="mst-launch" onClick={() => setOpen(true)}>
        <span className="mst-launch-ic" aria-hidden="true">📊</span>
        <span className="mst-launch-t">
          {y !== thisYear && <><N>{y}</N>년 </>}<N>{m}</N>월 통계 보기
        </span>
        <span className="mst-launch-n"><N>{n}</N>권 ›</span>
      </button>
      {/* body 에 붙인다. 내 행성 레이어는 planetEnter(forwards)가 남긴 transform 때문에
          fixed 의 기준이 화면이 아니라 긴 레이어가 되어, 창이 화면 위로 벗어나 떴다. */}
      {open && createPortal(
        <div className="mst-sheet" onClick={() => setOpen(false)}>
          <div className="mst-sheet-box" role="dialog" aria-modal="true" aria-label="월별 통계"
            onClick={(e) => e.stopPropagation()}>
            <div className="mst-sheet-head">
              <span>월별 통계</span>
              <button className="mst-sheet-x" onClick={() => setOpen(false)} aria-label="닫기">✕</button>
            </div>
            <MonthlyStats {...props} />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
